import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from '../../../assets/icons';
import {
  Button,
  ButtonSize,
  ButtonType,
  CheckBox,
  ColorType,
  Dropdown,
  DropdownType,
  Label,
  LabelType,
} from '../../_ui';
import { getConnectorAbbr, getConnectorIcon } from '../utils';
import { ChargerLevel, FinderFilters, NetworkOption } from './types';
import { FINDER_CONNECTORS } from './utils/connectors';

interface NearbyChargerFiltersProps {
  filters: FinderFilters;
  onFiltersChange: (changes: Partial<FinderFilters>) => void;
  networkOptions: NetworkOption[];
  isSearching: boolean;
  onSearch: () => void;
}

interface SelectableItem<T> {
  id: T;
  selected: boolean;
}

const ANY_LEVEL = 'any';
type LevelOption = ChargerLevel | typeof ANY_LEVEL;
const LEVEL_OPTIONS: LevelOption[] = [
  ANY_LEVEL,
  ChargerLevel.LEVEL_1,
  ChargerLevel.LEVEL_2,
  ChargerLevel.LEVEL_3,
];
const DROPDOWN_HEADER_CLASS = 'bg-grey1 border-grey3 rounded h-[40px]';

const getSelectedIds = <T,>(items: SelectableItem<T>[]) =>
  items.filter((item) => item.selected).map((item) => item.id);

const FieldLabel = ({ text }: { text: string }) => (
  <Label text={text} type={LabelType.LABEL_S} color={ColorType.BLACK} />
);

export const NearbyChargerFilters = ({
  filters,
  onFiltersChange,
  networkOptions,
  isSearching,
  onSearch,
}: NearbyChargerFiltersProps) => {
  const { t } = useTranslation();

  const connectorItems = useMemo(
    () =>
      FINDER_CONNECTORS.map(({ type }) => ({
        id: type,
        label: getConnectorAbbr(type),
        icon: getConnectorIcon(type),
        selected: filters.connectorTypes.includes(type),
      })),
    [filters.connectorTypes],
  );

  const levelItems = useMemo(
    () =>
      LEVEL_OPTIONS.map((option) => ({
        id: option,
        label: t(
          option === ANY_LEVEL ? 'nearby_chargers_any_level' : `nearby_chargers_level_${option}`,
        ),
        selected: option === (filters.level ?? ANY_LEVEL),
      })),
    [filters.level, t],
  );

  const networkItems = useMemo(
    () =>
      networkOptions.map(({ id, name, siteCount }) => ({
        id,
        label: `${name ?? t('nearby_chargers_unknown_network')} (${siteCount})`,
        selected: filters.networkIds.includes(id),
      })),
    [networkOptions, filters.networkIds, t],
  );

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex flex-row gap-2'>
        <div className='flex flex-col gap-2 flex-1 min-w-0'>
          <FieldLabel text={t('nearby_chargers_connector')} />
          <Dropdown
            type={DropdownType.CHECKBOX}
            items={connectorItems}
            onItemClick={(items: SelectableItem<string>[]) =>
              onFiltersChange({ connectorTypes: getSelectedIds(items) })
            }
            placeholder={t('nearby_chargers_any_connector')}
            placeholderLabelType={LabelType.DROPDOWN_HEADER}
            labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
            headerHighLightClassName={DROPDOWN_HEADER_CLASS}
            chevdownIconHighlightColor={ColorType.GREY6}
            headerWidth='100%'
            showPillHeader
            dataTestId='nearby-chargers-connector'
          />
        </div>
        <div className='flex flex-col gap-2 w-[176px] shrink-0'>
          <FieldLabel text={t('nearby_chargers_level')} />
          <Dropdown
            items={levelItems}
            onItemClick={(item: SelectableItem<LevelOption>) =>
              onFiltersChange({ level: item.id === ANY_LEVEL ? undefined : item.id })
            }
            labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
            headerHighLightClassName={DROPDOWN_HEADER_CLASS}
            chevdownIconHighlightColor={ColorType.GREY6}
            headerWidth='100%'
            contentDivWidth={220}
            dataTestId='nearby-chargers-level'
          />
        </div>
      </div>

      <div className='flex flex-col gap-2'>
        <FieldLabel text={t('nearby_chargers_network')} />
        <Dropdown
          type={DropdownType.CHECKBOX}
          items={networkItems}
          onItemClick={(items: SelectableItem<number>[]) =>
            onFiltersChange({ networkIds: getSelectedIds(items) })
          }
          placeholder={t('nearby_chargers_any_network')}
          placeholderLabelType={LabelType.DROPDOWN_HEADER}
          labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
          headerHighLightClassName={DROPDOWN_HEADER_CLASS}
          chevdownIconHighlightColor={ColorType.GREY6}
          headerWidth='100%'
          disabled={!networkItems.length}
          showPillHeader
          dataTestId='nearby-chargers-network'
        />
      </div>

      <div className='flex flex-row items-center justify-between gap-2'>
        <CheckBox
          label={t('nearby_chargers_include_unknown')}
          selected={filters.includeUnknownStatus}
          onChange={(checked: boolean) => onFiltersChange({ includeUnknownStatus: checked })}
          labelType={LabelType.BODY3}
          dataTestId='nearby-chargers-include-unknown'
        />
        <Button
          label={t('nearby_chargers_search')}
          icon={Search}
          iconColor={ColorType.WHITE}
          size={ButtonSize.SMALL}
          type={ButtonType.PRIMARY}
          onClick={onSearch}
          disabled={isSearching}
          isLoading={false}
          dataTestId='nearby-chargers-search'
        />
      </div>
    </div>
  );
};
